import { useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Nav, Footer, Eyebrow, ClosingV5 } from "../App";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";
import { PRIVACY_DEFAULTS, TERMS_DEFAULTS } from "../admin/contentDefaults";

/* ============================================================
   /privacy and /terms — generic legal page
   Same template, different CMS doc. Body is markdown.
============================================================ */
export default function LegalPage({ contentKey }) {
  const cms = useContent(contentKey);
  const fallback = contentKey === "privacy" ? PRIVACY_DEFAULTS : TERMS_DEFAULTS;
  const h = (cms && cms.header && Object.keys(cms.header).length ? cms.header : fallback.header) || {};
  const b = (cms && cms.body && (cms.body.markdown || "").trim() ? cms.body : fallback.body) || {};
  const breadcrumb = h.breadcrumb || (contentKey === "privacy" ? "Privacy Policy" : "Terms & Conditions");
  const eyebrow = h.eyebrow || "Legal";
  const title = h.title || (contentKey === "privacy" ? "Privacy Policy" : "Terms & Conditions");
  const lastUpdated = h.last_updated || "";
  const md = (b && b.markdown) || "";

  useSeo({
    route: contentKey === "privacy" ? "/privacy" : "/terms",
    fallback: { title: `${title} — Sunday Digital`, description: `${title} — Sunday Digital` },
  });
  useEffect(() => {
    document.title = `${title} — Sunday Digital`;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title]);

  return (
    <div
      data-testid={`legal-page-${contentKey}`}
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        {/* Header */}
        <section className="relative overflow-hidden bg-[#0B0F14]">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(900px 600px at 80% 30%, rgba(244,214,78,0.08), transparent 60%)",
            }}
          />
          <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-20">
            <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12 md:mb-16">
              <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
              <span className="w-1 h-1 rounded-full bg-neutral-700" />
              <span className="text-[#F4D64E]">{breadcrumb}</span>
            </div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1
              data-testid="legal-title"
              className="font-display mt-6 text-white leading-[0.95] tracking-[-0.04em] font-black text-[2.75rem] sm:text-[3.5rem] md:text-[5rem]"
            >
              {title}
            </h1>
            {lastUpdated ? (
              <p className="mt-7 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
                Last updated · {lastUpdated}
              </p>
            ) : null}
          </div>
        </section>

        {/* Body */}
        <section className="bg-[#0B0F14] border-t border-white/[0.06]">
          <div
            data-testid="legal-body"
            className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 prose-legal"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...rest }) => (
                  <a
                    {...rest}
                    href={href}
                    target={/^https?:/i.test(href || "") ? "_blank" : undefined}
                    rel={/^https?:/i.test(href || "") ? "noopener noreferrer" : undefined}
                    className="text-[#F4D64E] hover:text-white underline-offset-4 hover:underline"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {md}
            </ReactMarkdown>
          </div>
        </section>

        <ClosingV5 />
      </main>
      <Footer />

      {/* Scoped typography for the markdown body — kept inside the
          component so legal copy reads like an editorial article
          without leaking styles into the rest of the site. */}
      <style>{`
        .prose-legal { font-size: 1.05rem; line-height: 1.75; color: rgb(212 212 212); }
        .prose-legal h1 { font-family: var(--font-display, "ABC Repro", system-ui, sans-serif); font-size: clamp(1.85rem, 3.5vw, 2.75rem); font-weight: 900; letter-spacing: -0.025em; line-height: 1.05; color: white; margin-top: 2.25em; margin-bottom: 0.65em; }
        .prose-legal h2 { font-family: var(--font-display, "ABC Repro", system-ui, sans-serif); font-size: clamp(1.45rem, 2.5vw, 1.85rem); font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; color: white; margin-top: 2em; margin-bottom: 0.6em; padding-top: 0.4em; border-top: 1px solid rgba(255,255,255,0.06); }
        .prose-legal h2:first-child { border-top: 0; padding-top: 0; margin-top: 0; }
        .prose-legal h3 { font-family: var(--font-display, "ABC Repro", system-ui, sans-serif); font-size: clamp(1.15rem, 1.8vw, 1.35rem); font-weight: 700; letter-spacing: -0.01em; color: white; margin-top: 1.6em; margin-bottom: 0.4em; }
        .prose-legal p { margin: 0 0 1.4em 0; max-width: 70ch; }
        .prose-legal ul, .prose-legal ol { margin: 0 0 1.6em 0; padding-left: 1.5em; }
        .prose-legal ul li, .prose-legal ol li { margin-bottom: 0.5em; }
        .prose-legal ul li::marker { color: #F4D64E; }
        .prose-legal ol li::marker { color: #F4D64E; font-weight: bold; }
        .prose-legal strong { color: white; font-weight: 700; }
        .prose-legal em { color: rgb(229 229 229); font-style: italic; }
        .prose-legal blockquote { border-left: 2px solid #F4D64E; padding-left: 1.25em; margin: 1.5em 0; color: rgb(229 229 229); font-style: italic; }
        .prose-legal code { background: rgba(255,255,255,0.06); padding: 0.15em 0.4em; border-radius: 4px; font-size: 0.92em; }
        .prose-legal hr { border: 0; border-top: 1px solid rgba(255,255,255,0.08); margin: 2.5em 0; }
      `}</style>
    </div>
  );
}
